import React from 'react'
import { t, Trans } from '@lingui/macro'

import { useCachedWorkspacesQuery } from 'front-end/src/app/stores/useCachedWorkspacesQuery'
import { Box, ButtonDanger, ButtonLight, Text } from 'front-end/src/components'
import { CampaignModel } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { linkBuilder, staticLinks } from '../../../../shared/linkBuilder'
import { navigateTo } from '../../app/util/appHistory'
import { Modal } from '../../components/common/modal/Modal'
import { useDeleteWorkspaceMutation } from './DeleteWorkspaceModal.codegen'
import {
  RelatedInstanceItem,
  RelatedInstancesWrapper
} from 'front-end/src/components/common/related-instances/RelatedInstances'
import { brandColors } from 'shared/basicPalette'
import IconEnergy from 'front-end/src/components/common/icon/IconEnergy'
import { IndirectWorkspaceRelatedCampaigns } from 'front-end/src/graphql/public/PublicSchemaTypes'

interface IDeleteWorkspaceModalProps {
  workspaceId: number
  deleteModalVisible: boolean
  setDeletedModalVisible: (boolean) => void
  relatedActiveCampaigns: Array<Partial<CampaignModel> & { author: any }>
  indirectRelatedCampaigns: IndirectWorkspaceRelatedCampaigns
}

export const DeleteWorkspaceModal = ({
  workspaceId,
  deleteModalVisible,
  setDeletedModalVisible,
  relatedActiveCampaigns,
  indirectRelatedCampaigns
}: IDeleteWorkspaceModalProps) => {
  const [deleteWorkspace] = useDeleteWorkspaceMutation()
  const { refetch } = useCachedWorkspacesQuery()

  if (
    indirectRelatedCampaigns.filterRelatedCampaigns?.length ||
    indirectRelatedCampaigns.actionRelatedCampaigns?.length
  ) {
    const indirectRelatedCampaignsNoDuplicates = [
      ...indirectRelatedCampaigns.filterRelatedCampaigns,
      ...indirectRelatedCampaigns.actionRelatedCampaigns
    ].filter((item, index, self) => {
      return self.findIndex((t) => t.id === item.id) === index
    })

    return (
      <Modal
        newDesign
        header={t`Unable to archive workspace`}
        show={deleteModalVisible}
        controls={[
          <ButtonLight onClick={() => setDeletedModalVisible(false)} key={2}>
            <Trans>Cancel</Trans>
          </ButtonLight>
        ]}
        onHide={() => setDeletedModalVisible(false)}
      >
        <Box mb="16px">
          <Text className={`Modal__relatedItems__importantInfo`}>
            <Trans>
              This workspace cannot be archived as it is currently used within
              the actions and/or filters in the campaign(s) listed below.
            </Trans>
          </Text>
          <Text className={`Modal__relatedItems__importantInfo`}>
            <Trans>
              Should you still wish to archive this workspace, please update the
              relevant campaigns or contact their owner.
            </Trans>
          </Text>
        </Box>

        <RelatedInstancesWrapper>
          {indirectRelatedCampaignsNoDuplicates.map((campaign) => {
            return (
              <RelatedInstanceItem
                key={`campaign-${campaign.id}`}
                id={campaign.id}
                href={`${linkBuilder.campaign(campaign.id)}`}
                icon={<IconEnergy color={brandColors.grey.mid} />}
                name={campaign.name}
                additionalInfo={campaign.author?.userIdentity}
                tooltip={t`Campaign`}
              />
            )
          })}
        </RelatedInstancesWrapper>
      </Modal>
    )
  }

  return (
    <Modal
      header={t`Archive workspace`}
      className="DeleteWorkspaceModal"
      show={deleteModalVisible}
      controls={[
        <ButtonLight onClick={() => setDeletedModalVisible(false)} key={1}>
          <Trans>Cancel</Trans>
        </ButtonLight>,
        <ButtonDanger
          key={2}
          onClickAsync={async () => {
            await deleteWorkspace({
              variables: {
                workspaceId
              }
            })
            refetch()
            navigateTo(staticLinks.home)
          }}
        >
          <Trans>Archive this workspace</Trans>
        </ButtonDanger>
      ]}
      onHide={() => setDeletedModalVisible(false)}
    >
      <p>
        <Trans>
          Archiving this workspace will remove access to any resources within
          it, and archive any existing records of learning undertaken within it.
        </Trans>
      </p>
      {relatedActiveCampaigns && relatedActiveCampaigns.length > 0 && (
        <>
          <br />
          <p>
            <Trans>
              This workspace appears in some campaigns' filters. Deleting this
              workspace will result in deactivating these campaigns:
            </Trans>
          </p>
          <ul>
            {relatedActiveCampaigns.map((campaign) => {
              return (
                <li key={campaign.id}>
                  {campaign.name} (
                  <b>
                    {campaign.author.email
                      ? campaign.author.email
                      : campaign.author.account_name}
                  </b>
                  )
                </li>
              )
            })}
          </ul>
        </>
      )}
      <br />
      <p>
        <strong>
          <Trans>This action cannot be undone</Trans>
        </strong>
      </p>
    </Modal>
  )
}
